import { AlertTriangle, ArrowRight, ChevronRight, OctagonX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import { cn } from '~/utils/cn';
import { formatDurationMs, formatRelative } from '~/utils/date';

import type {
  FunnelProductsQuery,
  FunnelReason,
  FunnelStage,
} from '../../types/batch-funnel.api.types';
import {
  FUNNEL_DRILLABLE_STAGES,
  FUNNEL_TONE_CHIP,
  STAGE_BREAKDOWN_FIELDS,
  formatFunnelCount,
  formatFunnelUsd,
  reasonKindTone,
  type FunnelTone,
} from '../../utils/batch-funnel';
import type { StageErrorTally } from '../../utils/stage-errors';

/** Reason kinds with a per-product list behind them; NOT_ATTEMPTED / UNEXPLAINED have none. */
const DRILLABLE_KINDS: ReadonlySet<string> = new Set(['FAILED', 'DROPPED', 'EXCLUDED']);

/** Stage status → chip tone. */
const STATUS_TONE: Record<string, FunnelTone> = {
  completed: 'success',
  in_progress: 'info',
  awaiting: 'warning',
  scheduled: 'neutral',
  skipped: 'neutral',
  suppressed: 'warning',
  failed: 'error',
  rejected: 'error',
};

interface FunnelStagesProps {
  stages: FunnelStage[];
  /** Opens the products drill-in for a reason chip. */
  onDrill: (title: string, query: FunnelProductsQuery) => void;
  /** Current stage errors per funnel stage key (⛔ / ⚠ chips, stage-errors §5.2). */
  errorTally?: Map<string, StageErrorTally>;
  /** lily.ai: usage facts carry their $ cost; customers see tokens only. */
  showCost?: boolean;
}

/**
 * Funnel stage cards (RECEIVED → … → DELIVERED): in → out, where the rest went
 * (excluded / dropped / failed / not attempted / in flight / unexplained) and
 * why (reason chips → product drill-in). Machine time and time waiting on a
 * person are shown apart. Counts are never summed client-side (§13.2).
 */
export function FunnelStages({ stages, onDrill, errorTally, showCost = false }: FunnelStagesProps) {
  const { t } = useTranslation('catalog-operations');
  if (stages.length === 0) return null;
  // Stage errors on steps the funnel has no card for (quality score, sample,
  // approval) still need a home — a chip row above the cards.
  const stageKeys = new Set(stages.map((s) => s.key));
  const orphanTallies = [...(errorTally ?? new Map<string, StageErrorTally>())].filter(
    ([key]) => !stageKeys.has(key),
  );
  return (
    <Card data-testid="batch-funnel-stages-card">
      <CardHeader className="p-6 pb-3">
        <h2 className="text-xl" data-testid="batch-funnel-stages-title">
          {t('batchFunnel.stages.title')}
        </h2>
        <p className="text-muted-foreground text-sm font-light">
          {t('batchFunnel.stages.subtitle')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3 px-6 pb-6">
        {orphanTallies.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-2 text-xs"
            data-testid="batch-funnel-stages-other-steps"
          >
            <span className="text-muted-foreground">{t('stageErrors.otherSteps')}</span>
            {orphanTallies.map(([key, tally]) => (
              <span
                key={key}
                data-testid={`batch-funnel-stages-other-step-badge-${key}`}
                className="bg-background inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5"
              >
                <span className="text-foreground">
                  {t(`stageErrors.funnelStage.${key}`, { defaultValue: key })}
                </span>
                <StageErrorChips tally={tally} testId={`batch-funnel-stages-other-step-${key}`} />
              </span>
            ))}
          </div>
        )}
        <div
          className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(13rem,1fr))]"
          data-testid="batch-funnel-stages-list"
        >
          {stages.map((stage, index) => (
            <StageCard
              key={stage.key ?? index}
              stage={stage}
              onDrill={onDrill}
              tally={stage.key ? errorTally?.get(stage.key) : undefined}
              showCost={showCost}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** One stage: header chip, in → out, breakdown, reason chips, timing + retry facts. */
function StageCard({
  stage,
  onDrill,
  tally,
  showCost,
}: {
  stage: FunnelStage;
  onDrill: FunnelStagesProps['onDrill'];
  tally: StageErrorTally | undefined;
  showCost: boolean;
}) {
  const { t } = useTranslation('catalog-operations');
  const status = stage.status ?? '';
  const suppressed = status === 'suppressed';
  const label = stage.label ?? stage.key ?? '—';
  const breakdown = STAGE_BREAKDOWN_FIELDS.filter(({ field }) => stage[field] > 0);
  const drillable = FUNNEL_DRILLABLE_STAGES.has(stage.key ?? '');
  const stageId = `batch-funnel-stage-${stage.key ?? 'unknown'}`;

  /** Reason chip click → products behind that reason at this stage. */
  function drillReason(reason: FunnelReason) {
    onDrill(`${label} · ${reason.label ?? reason.code ?? ''}`, {
      stage: stage.key,
      outcome: 'FAILED',
      reason: reason.code,
    });
  }

  return (
    <div
      className="bg-background relative flex flex-col gap-3 rounded-xl border p-4"
      data-testid={`${stageId}-card`}
    >
      <div className="flex items-start justify-between gap-2">
        <p
          className="text-foreground flex flex-wrap items-center gap-1.5 text-sm font-semibold"
          data-testid={`${stageId}-title`}
        >
          {label}
          {tally && <StageErrorChips tally={tally} testId={stageId} />}
        </p>
        <span
          data-testid={`${stageId}-status-badge`}
          className={cn(
            'rounded-md px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
            FUNNEL_TONE_CHIP[STATUS_TONE[status] ?? 'neutral'],
          )}
        >
          {t(`batchFunnel.stageStatus.${status || 'unknown'}`, { defaultValue: status || '—' })}
        </span>
      </div>

      {/* Rule §13.6: a suppressed delivery never reads as "Delivered". */}
      {suppressed && (
        <p
          className="text-warning text-xs font-medium"
          data-testid={`${stageId}-suppressed-banner`}
        >
          {t('batchFunnel.stages.suppressed')}
        </p>
      )}

      <div className="flex items-baseline gap-2 tabular-nums">
        <span className="text-foreground text-[22px] font-bold" data-testid={`${stageId}-in-value`}>
          {formatFunnelCount(stage.in)}
        </span>
        <ArrowRight className="text-muted-foreground h-4 w-4 self-center" aria-hidden="true" />
        <span
          className="text-foreground text-[22px] font-bold"
          data-testid={`${stageId}-out-value`}
        >
          {formatFunnelCount(stage.out)}
        </span>
      </div>
      <p className="text-muted-foreground -mt-2 text-xs">{t('batchFunnel.stages.inOut')}</p>

      {breakdown.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" data-testid={`${stageId}-breakdown-list`}>
          {breakdown.map(({ field, tone }) => (
            <li
              key={field}
              data-testid={`${stageId}-breakdown-${field}-badge`}
              className={cn(
                'rounded-md px-1.5 py-0.5 text-xs tabular-nums',
                FUNNEL_TONE_CHIP[tone],
              )}
            >
              {t(`batchFunnel.breakdown.${field}`, { count: stage[field] })}
            </li>
          ))}
        </ul>
      )}

      {(stage.reasons ?? []).length > 0 && (
        <ul className="space-y-1" data-testid={`${stageId}-reason-list`}>
          {(stage.reasons ?? []).map((reason) => {
            const reasonId = `${stageId}-reason-${reason.kind ?? 'unknown'}-${reason.code ?? 'unknown'}`;
            const canDrill = drillable && DRILLABLE_KINDS.has(reason.kind ?? '');
            const chipTone = FUNNEL_TONE_CHIP[reasonKindTone(reason.kind)];
            const content = (
              <>
                <span className="min-w-0 flex-1 truncate text-left">
                  {reason.label ?? reason.code}
                </span>
                <span className="font-semibold tabular-nums" data-testid={`${reasonId}-value`}>
                  {reason.count.toLocaleString()}
                </span>
                {canDrill && <ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />}
              </>
            );
            return (
              <li
                key={`${reason.kind ?? ''}:${reason.code ?? ''}`}
                data-testid={`${reasonId}-item`}
              >
                {canDrill ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'h-auto w-full cursor-pointer justify-start gap-1.5 rounded-md px-2 py-1 text-xs font-normal hover:opacity-80',
                      chipTone,
                    )}
                    title={reason.label ?? reason.code ?? undefined}
                    data-testid={`${reasonId}-button`}
                    onClick={() => {
                      drillReason(reason);
                    }}
                  >
                    {content}
                  </Button>
                ) : (
                  <div
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs',
                      chipTone,
                    )}
                    title={reason.label ?? reason.code ?? undefined}
                  >
                    {content}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <StageFacts stage={stage} showCost={showCost} testId={`${stageId}-facts-list`} />

      {stage.check === false && (
        <p
          className="text-warning flex items-center gap-1 text-xs"
          data-testid={`${stageId}-check-failed-banner`}
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('batchFunnel.stages.checkFailed')}
        </p>
      )}
    </div>
  );
}

/** Timing, ETA, retry recovery, token usage and live-check facts for a stage. */
function StageFacts({
  stage,
  showCost,
  testId,
}: {
  stage: FunnelStage;
  showCost: boolean;
  testId: string;
}) {
  const { t } = useTranslation('catalog-operations');
  const facts: string[] = [];
  if (stage.durationMillis != null) {
    facts.push(
      t('batchFunnel.facts.machineTime', { duration: formatDurationMs(stage.durationMillis) }),
    );
  }
  if (stage.waitedOnPeopleMillis != null && stage.waitedOnPeopleMillis > 0) {
    facts.push(
      t('batchFunnel.facts.waitedOnPeople', {
        duration: formatDurationMs(stage.waitedOnPeopleMillis),
      }),
    );
  }
  if (stage.etaSeconds != null) {
    facts.push(t('batchFunnel.facts.eta', { duration: formatDurationMs(stage.etaSeconds * 1000) }));
  }
  if (stage.recoveredAfterRetry != null && stage.recoveredAfterRetry > 0) {
    facts.push(t('batchFunnel.facts.recovered', { count: stage.recoveredAfterRetry }));
  }
  if (stage.retryRuns != null && stage.retryRuns > 0) {
    facts.push(t('batchFunnel.facts.retryRuns', { count: stage.retryRuns }));
  }
  if (stage.retriedProducts != null && stage.retriedProducts > 0) {
    facts.push(t('batchFunnel.facts.retriedProducts', { count: stage.retriedProducts }));
  }
  // Zeroed usage (`productsWithUsage: 0`) means no telemetry, not "0 tokens".
  if (stage.usage && stage.usage.productsWithUsage > 0) {
    const tokens = (stage.usage.inputTokens + stage.usage.outputTokens).toLocaleString();
    facts.push(
      showCost
        ? t('batchFunnel.facts.usage', { tokens, cost: formatFunnelUsd(stage.usage.costUsd) })
        : t('batchFunnel.facts.tokens', { tokens }),
    );
  }
  if (stage.liveCheckedAt) {
    facts.push(t('batchFunnel.facts.liveChecked', { when: formatRelative(stage.liveCheckedAt) }));
  }
  if (facts.length === 0) return null;
  return (
    <ul
      className="text-muted-foreground mt-auto space-y-0.5 border-t pt-2 text-xs"
      data-testid={testId}
    >
      {facts.map((fact) => (
        <li key={fact}>{fact}</li>
      ))}
    </ul>
  );
}

/** ⛔ when the run stopped at this stage, ⚠N for warnings that left output missing. */
function StageErrorChips({
  tally,
  testId,
}: {
  tally: StageErrorTally;
  /** Prefix; chips get `${testId}-error-badge` / `${testId}-warning-badge`. */
  testId: string;
}) {
  const { t } = useTranslation('catalog-operations');
  return (
    <>
      {tally.errors > 0 && (
        <span
          data-testid={`${testId}-error-badge`}
          className={cn(
            'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] font-medium',
            FUNNEL_TONE_CHIP.error,
          )}
          title={t('stageErrors.chipErrors', { count: tally.errors })}
        >
          <OctagonX className="h-3 w-3" aria-hidden="true" />
          <span className="sr-only">{t('stageErrors.chipErrors', { count: tally.errors })}</span>
        </span>
      )}
      {tally.warnings > 0 && (
        <span
          data-testid={`${testId}-warning-badge`}
          className={cn(
            'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] font-medium tabular-nums',
            FUNNEL_TONE_CHIP.warning,
          )}
          title={t('stageErrors.chipWarnings', { count: tally.warnings })}
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          {tally.warnings}
          <span className="sr-only">
            {t('stageErrors.chipWarnings', { count: tally.warnings })}
          </span>
        </span>
      )}
    </>
  );
}
